import {
  Backdrop,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTheme } from '@emotion/react';
import CreateContractModal from '../components/CreateContractModal';

import api from '../../../services/api';
import { tokens } from '../../../theme';
import Header from '../../../components/Header';
import DadosSimulacaoTab from '../components/DadosSimulacaoTab';
import DadosClienteTab from '../components/DadosClienteTab';
import ConsultaCreditoTab from '../components/ConsultaCreditoTab';
import { useTranslation } from '../../../hooks/useTranslation';

const SimulationDetailSuperAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [contractModalOpen, setContractModalOpen] = useState(false);

  const fetchSimulation = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/v1/simulation-super-adm/${id}`);
      setSimulation(data?.data ?? null);
    } catch (error) {
      console.error('Erro ao buscar simulação:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSimulation();
    setActiveTab(0);
  }, [fetchSimulation]);

  if (loading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Box
        component="button"
        onClick={() => navigate('/app/simulation-adm')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: colors.grey[300],
          fontSize: '14px',
          mb: 3,
          p: 0,
          '&:hover': { color: colors.greenAccent[400] },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        {t('simulation.adm.detail.backButton')}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Header
            title={t('simulation.adm.detail.title')}
            subtitle={t('simulation.adm.detail.subtitle')}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
          <Box
            component="button"
            onClick={() => !simulation?.has_contract && setContractModalOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.5,
              py: 1,
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              backgroundColor: '#4caf50',
              color: '#fff',
              '&:hover': { backgroundColor: '#388e3c' },
              transition: 'background-color 0.2s',
              width: {
                xs: '100%',
                md: 'auto',
              }
            }}
          >
            <CheckIcon sx={{ fontSize: 16 }} />
            {t('simulation.adm.detail.approve')}
          </Box>

          <Box
            component="button"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.5,
              py: 1,
              borderRadius: '6px',
              border: '1px solid #bdbdbd',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              backgroundColor: '#fff',
              color: '#616161',
              '&:hover': { backgroundColor: '#f5f5f5' },
              transition: 'background-color 0.2s',
              width: {
                xs: '100%', 
                md: 'auto', 
              }
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
            {t('simulation.adm.detail.requestAdjustment')}
          </Box>

          <Box
            component="button"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.5,
              py: 1,
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              backgroundColor: '#f44336',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' },
              transition: 'background-color 0.2s',
              width: {
                xs: '100%', 
                md: 'auto', 
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
            {t('simulation.adm.detail.reject')}
          </Box>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 3,
            borderBottom: '1px solid #e5e7eb',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: 14 },
            '& .Mui-selected': { color: '#000', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: colors.greenAccent[500], height: 3 },
          }}
        >
          <Tab label={t('simulation.adm.detail.tab')} />
          <Tab label={t('simulation.adm.detail.tabClient')} />
          <Tab label={t('simulation.adm.detail.tabConsult')} />
        </Tabs>

        <Box p={3}>
          {activeTab === 0 && <DadosSimulacaoTab simulation={simulation} />}
          {activeTab === 1 && (
            <DadosClienteTab
              simulation={simulation}
              onNavigateToSimulation={(id) => navigate(`/app/simulation-adm/${id}`)}
            />
          )}
          {activeTab === 2 && <ConsultaCreditoTab simulation={simulation} />}
        </Box>
      </Paper>

      <CreateContractModal
        open={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        simulation={simulation}
      />
    </Box>
  );
};

export default SimulationDetailSuperAdmin;
